import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listMerchantMemberships, showToast, switchAuthContext } from '@beatq/shared';
import {
  branchFromEnvelope,
  branchesFromEnvelope,
  getBranch,
  isBranchPaused,
  listBranches,
  type BranchSummary,
} from '../features/branch-settings/branchSettingsApi.js';
import { setStoredBranchId } from '../features/branch-settings/branchSettingsState.js';
import { businessFromEnvelope, getBusiness } from '../features/business/businessApi.js';
import {
  getOrderBoard,
  isOperationallyLiveOrder,
  ordersFromEnvelope,
} from '../features/order-board/orderBoardApi.js';
import { currentMerchantScope, getApiBaseUrl } from './merchantContext.js';
import {
  headerLiveStatus,
  isMerchantProductionBuild,
  merchantRuntimeLabel,
  openedBranchOptionDetail,
  shouldShowBranchSelector,
  shouldShowBusinessSelector,
  tradingLabel,
} from './merchantChrome.js';
import { MerchantScopeSelect } from './MerchantScopeSelect.js';
import { merchantOpsPollMs } from '../features/order-board/useMerchantOrderRealtime.js';

export function MerchantStoreSelector({
  liveOrderCount,
  socketsLive = false,
  showBranchName = true,
}: {
  liveOrderCount?: number;
  socketsLive?: boolean;
  showBranchName?: boolean;
}) {
  const qc = useQueryClient();
  const scope = currentMerchantScope();

  const membershipsQuery = useQuery({
    queryKey: ['merchant', 'memberships'],
    queryFn: () => listMerchantMemberships(getApiBaseUrl()),
    staleTime: 60_000,
  });
  const businessQuery = useQuery({
    queryKey: ['merchant', 'business'],
    queryFn: getBusiness,
    staleTime: 60_000,
  });
  const branchesQuery = useQuery({
    queryKey: ['merchant', 'branches'],
    queryFn: listBranches,
    staleTime: 60_000,
  });
  const boardQuery = useQuery({
    queryKey: ['merchant', 'orders', 'board'],
    queryFn: () => getOrderBoard(),
    refetchInterval: merchantOpsPollMs(socketsLive, 15_000),
  });
  const branchQuery = useQuery({
    queryKey: ['merchant', 'branch', scope.branchId],
    queryFn: () => getBranch(scope.branchId),
    enabled: !!scope.branchId,
    refetchInterval: 60_000,
  });

  const memberships = membershipsQuery.data ?? [];
  const currentBusiness = businessFromEnvelope(businessQuery.data);
  const branches = branchesFromEnvelope(branchesQuery.data);
  const membershipBranches: BranchSummary[] =
    memberships
      .find((membership) => membership.businessId === scope.businessId)
      ?.branches?.map((branch) => ({
        id: branch.branchId,
        name: branch.name,
        status: branch.status,
        tradingState: branch.status,
        publicReference: null,
        liveOrderCount: null,
      })) ?? [];
  const selectableBranches = branches.length > 0 ? branches : membershipBranches;
  const liveCount =
    liveOrderCount ??
    ordersFromEnvelope(boardQuery.data).filter((order) => isOperationallyLiveOrder(order)).length;
  const branchDetail = branchFromEnvelope(branchQuery.data);
  const paused = isBranchPaused(branchDetail);
  const activeFromList =
    selectableBranches.find((branch) => branch.id === scope.branchId) ?? selectableBranches[0];
  const tradingState = paused
    ? 'PAUSED'
    : (activeFromList?.tradingState ??
      activeFromList?.status ??
      branchDetail?.tradingState ??
      branchDetail?.status);

  const businessOptions =
    memberships.length > 0
      ? memberships
      : [
          {
            businessId: scope.businessId,
            tradingName: currentBusiness?.tradingName || currentBusiness?.legalName || 'Business',
            branches: [],
          },
        ];

  const switchScope = useMutation({
    mutationFn: async (next: { businessId: string; branchId?: string }) => {
      await switchAuthContext(getApiBaseUrl(), 'merchant', next);
      return next;
    },
    onSuccess: (next) => {
      if (next.branchId) setStoredBranchId(next.branchId);
      void qc.invalidateQueries();
      showToast('success', next.branchId ? 'Branch switched' : 'Business switched');
    },
    onError: (err) => {
      showToast('error', err instanceof Error ? err.message : 'Could not switch scope');
    },
  });

  const branchValue = selectableBranches.some((branch) => branch.id === scope.branchId)
    ? scope.branchId
    : (selectableBranches[0]?.id ?? scope.branchId);
  const businessValue = businessOptions.some((b) => b.businessId === scope.businessId)
    ? scope.businessId
    : (businessOptions[0]?.businessId ?? scope.businessId);

  const busy = switchScope.isPending || branchesQuery.isLoading;
  const showBusiness = shouldShowBusinessSelector(businessOptions.length);
  const showBranch = shouldShowBranchSelector(selectableBranches.length);
  const liveText = headerLiveStatus({
    tradingState,
    liveCount,
    branchName: showBranch || !showBranchName ? undefined : (activeFromList?.name ?? undefined),
  });
  const runtimeText = merchantRuntimeLabel({
    production: isMerchantProductionBuild(),
    socketsLive,
  });

  return (
    <div className="m01-scope-selectors" data-testid="merchant-scope-selectors">
      {showBusiness ? (
        <MerchantScopeSelect
          ariaLabel="Business"
          testId="merchant-business-selector"
          value={businessValue}
          disabled={busy || businessOptions.length === 0}
          options={businessOptions.map((biz) => ({
            value: biz.businessId,
            name: biz.tradingName || biz.businessId,
          }))}
          onChange={(businessId) => {
            if (!businessId || businessId === scope.businessId) return;
            const membership = memberships.find((m) => m.businessId === businessId);
            const nextBranch =
              membership?.branches?.find((b) => b.status === 'ACTIVE')?.branchId ??
              membership?.branches?.[0]?.branchId;
            switchScope.mutate({ businessId, branchId: nextBranch });
          }}
        />
      ) : null}
      {showBranch ? (
        <MerchantScopeSelect
          ariaLabel="Branch"
          testId="merchant-store-selector"
          branchSelector
          value={branchValue}
          disabled={busy}
          options={selectableBranches.map((b) => {
            const live =
              typeof b.liveOrderCount === 'number'
                ? b.liveOrderCount
                : b.id === scope.branchId
                  ? liveCount
                  : null;
            const state = tradingLabel(b.tradingState ?? b.status);
            return {
              value: b.id ?? '',
              name: b.name || b.publicReference || b.id || 'Branch',
              detail: openedBranchOptionDetail(state, live),
            };
          })}
          onChange={(next) => {
            if (next && next !== scope.branchId) {
              switchScope.mutate({ businessId: scope.businessId, branchId: next });
            }
          }}
        />
      ) : null}
      <p className="mer-live-status" data-testid="merchant-live-status">
        {liveText}
      </p>
      <p
        className="mer-runtime-status"
        data-testid="merchant-runtime-status"
        data-sockets={socketsLive ? 'live' : 'poll'}
        title={runtimeText}
      >
        {runtimeText}
      </p>
    </div>
  );
}
